import { createContext, useContext } from "react";

// #1788 — the venue timezone for whatever show this board is rendering.
//
// A context rather than a prop because the two things that most need it are
// the furthest from the data: VenueClock takes no props at all (it is
// dropped into ScoreboardFrame's bottom-right slot), and ClassHeader sits a
// layout component down (RankedListLayout, SingleEntryLayout) from the page
// that owns the poll.
// Threading a `timeZone` prop through all of those would mean every future
// layout has to remember to forward it, and the failure mode of forgetting
// is silent — the board renders a plausible time that is simply the wrong
// city's. A context makes the default explicit in exactly one place.
//
// The value is ALREADY RESOLVED by score-api (scoreboard-public.js sends
// `venueTimezone` as a string Intl accepts). #3324: it is THREE-STATE, and
// the old silent Pacific default is gone:
//   - undefined: not loaded yet (no poll has landed, or a page that does not
//     provide the context). Consumers render nothing for the zone-dependent
//     bit rather than guess.
//   - null: loaded, and the show has NO usable zone. Consumers render the
//     calm explicit "Venue timezone not set" (VENUE_TIME_UNSET_TEXT).
//   - string: the configured zone.
// The provider passes the value through untouched; it must NOT coalesce
// null/undefined to a zone, that coalescing was the bug.
const VenueTimeZoneContext = createContext(undefined);

export function VenueTimeZoneProvider({ timeZone, children }) {
  return <VenueTimeZoneContext.Provider value={timeZone}>{children}</VenueTimeZoneContext.Provider>;
}

export function useVenueTimeZone() {
  return useContext(VenueTimeZoneContext);
}

export default VenueTimeZoneContext;
